import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import type { AbstractControl, ValidationErrors } from '@angular/forms';
import * as countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import parsePhoneNumberFromString, {
  CountryCode,
  getCountryCallingCode,
  parsePhoneNumber,
  PhoneNumber,
} from 'libphonenumber-js';

countries.registerLocale(enLocale);

// function to get country code
type CountryCodeInfo = {
  countryCode: string;
};

export function getCountryCode(countryName: string): CountryCodeInfo | null {
  const countryCode = countries.getAlpha2Code(countryName, 'en');
  return countryCode ? { countryCode } : null;
}

export const countriesOperator = countries;

// get the calling code for a country
export function getCallingCode(country: string): string {
  const countryCode = getCountryCode(country)?.countryCode;
  return `+${getCountryCallingCode(countryCode as CountryCode)}`;
}

export const getParsedCountryNumber = (phoneNumber: string) => {
  try {
    const parsedNumber = parsePhoneNumber(phoneNumber);
    return parsedNumber;
  } catch (error) {
    return null;
  }
};

// tailwind merge function
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// function to rename the key of an object
export function renameKey(
  obj: Record<string, any>,
  oldKey: string,
  newKey: any
): Record<string, any> {
  if (oldKey !== newKey && obj.hasOwnProperty(oldKey)) {
    obj[newKey] = obj[oldKey];
    delete obj[oldKey];
  }
  return obj;
}

// takes any string and replaces the _ with " "
export function replaceUnderscores(input: string = ''): string {
  return input.replace(/_/g, ' ');
}

// function to check if a smaller array is a subset of a larger array
export function isSubset(subset: any[], superset: any[]): boolean {
  if (!Array.isArray(subset) || !Array.isArray(superset)) {
    return false;
  }

  // Detect if subset contains objects with an 'id' property
  const isObjectArray =
    subset.length > 0 &&
    typeof subset[0] === 'object' &&
    subset[0] !== null &&
    'id' in subset[0];

  if (isObjectArray) {
    const supersetIds = superset.map((item) =>
      item && typeof item === 'object' ? (item as any).id : undefined
    );
    return subset.every((item) => supersetIds.includes((item as any).id));
  } else {
    return subset.every((value: unknown) => superset.includes(value));
  }
}

export function eligibleParams(params: Record<string, unknown>) {
  const updatedParams: Record<string, any> = {};
  for (let key in params) {
    if (params[key] || params[key] == 0) {
      updatedParams[key] = params[key];
    }
  }

  return params ? updatedParams : {};
}

export function convertTo12HourFormat(time24: string) {
  if (!time24) {
    return '';
  }

  const [hours, minutes] = time24.split(':');
  let hour = parseInt(hours);
  const period = hour >= 12 ? 'PM' : 'AM';

  // Adjust hours for 12-hour format
  hour = hour % 12 || 12;

  return `${hour}:${minutes} ${period}`;
}

export function countSMSCharacters(message: string): {
  totalCharacters: number;
  encoding: 'GSM-7' | 'Unicode';
  singleSMSLimit: number;
  segments: number;
} {
  const gsm7Characters =
    '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞ\b^{}\\[~]|€ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà';
  const isGSM7 = [...message].every((char) => gsm7Characters.includes(char));

  const totalCharacters = message.length;
  const encoding = isGSM7 ? 'GSM-7' : 'Unicode';
  const singleSMSLimit = encoding === 'GSM-7' ? 160 : 70;
  const segmentLimit = encoding === 'GSM-7' ? 153 : 67;

  // A message that fits the single-SMS limit is one segment; only once it
  // overflows is it split into multipart segments (which lose space to the UDH)
  const segments =
    totalCharacters <= singleSMSLimit
      ? Math.ceil(totalCharacters / singleSMSLimit)
      : Math.ceil(totalCharacters / segmentLimit);

  return {
    totalCharacters,
    encoding,
    singleSMSLimit,
    segments,
  };
}

// ---- Contact attributes / personalization placeholders -------------------
// Messages may embed contact attributes as `{{attributeName}}` placeholders
// (e.g. "Hi {{firstName}}"). These helpers extract/remove them and provide
// friendly labels, and are shared by the attribute chips + the send payloads.

// Friendly labels for the known contact attributes offered in the composer.
export const ATTRIBUTE_LABELS: Record<string, string> = {
  firstName: 'First Name',
  lastName: 'Last Name',
  fullName: 'Full Name',
  emailAddress: 'Email',
  primaryContactNumber: 'Phone Number',
  gender: 'Gender',
  birthDate: 'Birth Date',
  country: 'Country',
  city: 'City',
};

// The contact attributes offered by the composer's attribute picker, in order.
export const MESSAGE_ATTRIBUTE_KEYS: string[] = Object.keys(ATTRIBUTE_LABELS);

// matches `{{ attributeName }}` (tolerant of surrounding whitespace)
const ATTRIBUTE_PLACEHOLDER = /\{\{\s*([a-zA-Z0-9_ ]+?)\s*\}\}/g;

// Returns the unique attribute keys embedded in a message, in first-seen order.
export function extractMessageAttributes(message?: string | null): string[] {
  if (!message) return [];
  const found: string[] = [];
  for (const match of message.matchAll(ATTRIBUTE_PLACEHOLDER)) {
    const key = match[1];
    if (!found.includes(key)) {
      found.push(key);
    }
  }
  return found;
}

// Substitutes `{{attribute}}` placeholders with values from a row/record.
// Missing or empty values resolve to an empty string. Used for the live
// personalization preview (e.g. Smart Broadcast) where attributes come from a
// data file rather than the fixed contact set.
export function applyMessageTemplate(
  message: string | null | undefined,
  values: Record<string, string | number | null | undefined>
): string {
  if (!message) return '';
  return message.replace(ATTRIBUTE_PLACEHOLDER, (_match, key: string) => {
    const value = values?.[key];
    return value === undefined || value === null ? '' : String(value);
  });
}

// Removes every `{{attribute}}` placeholder for the given key from a message.
export function removeMessageAttribute(
  message: string | null | undefined,
  attribute: string
): string {
  if (!message) return '';
  const escaped = attribute.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return message.replace(
    new RegExp(`\\{\\{\\s*${escaped}\\s*\\}\\}`, 'g'),
    ''
  );
}

// Human-readable label for an attribute key (falls back to humanizing camelCase).
export function attributeLabel(key: string): string {
  return (
    ATTRIBUTE_LABELS[key] ||
    key
      .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
      .replace(/^./, (c) => c.toUpperCase())
  );
}

export function makeArrayUnique(arr: any[]) {
  if (!Array.isArray(arr)) {
    return [];
  }

  return [...new Set(arr)];
}
// Example usage:

export function isValidInternational(phone: string): boolean {
  const p = parsePhoneNumberFromString(phone);
  return !!p && p.isValid();
}

/**
 * Parses a phone number out of spreadsheet/CSV data, tolerating the very common
 * case where Excel (or another spreadsheet app) strips the leading "+" from an
 * international number — turning e.g. "+233241234567" into "233241234567".
 *
 * Strategy:
 *  1. A value that still has a "+" is trusted as international.
 *  2. Otherwise the bare digits are read two ways and whichever is *valid* wins:
 *       • national — parsed against `defaultCountry`, which handles a trunk "0"
 *         or a plain local number (e.g. "0241234567" → +233…);
 *       • international — the stripped "+" reconstructed as "+" + digits (only
 *         attempted when there's no leading "0", since E.164 has none).
 *  3. When *both* read as valid, the international reading is preferred only if
 *     the digits actually begin with `defaultCountry`'s calling code — i.e. the
 *     number really did carry its own country code. This stops a genuine
 *     national number (e.g. a US 10-digit number) being mis-read as foreign.
 *
 * Returns a valid `PhoneNumber`, or `undefined` when nothing parses.
 */
export function parseFlexiblePhoneNumber(
  raw: string | null | undefined,
  defaultCountry?: CountryCode
): PhoneNumber | undefined {
  const value = (raw ?? '').toString().trim();
  if (!value) return undefined;

  // 1. Already in international form — trust it as written.
  if (value.startsWith('+')) {
    const parsed = parsePhoneNumberFromString(value);
    return parsed?.isValid() ? parsed : undefined;
  }

  const digits = value.replace(/\D/g, '');
  if (!digits) return undefined;

  // 2a. National reading against the default country (covers trunk "0" / local).
  const nationalParsed = defaultCountry
    ? parsePhoneNumberFromString(digits, defaultCountry)
    : undefined;
  const national = nationalParsed?.isValid() ? nationalParsed : undefined;

  // 2b. Stripped-"+" reading. Skip when a trunk "0" is present — E.164 has none.
  const internationalParsed = !digits.startsWith('0')
    ? parsePhoneNumberFromString('+' + digits)
    : undefined;
  const international = internationalParsed?.isValid()
    ? internationalParsed
    : undefined;

  // 3. Disambiguate when both readings are valid.
  if (national && international) {
    if (defaultCountry && digits.startsWith(getCountryCallingCode(defaultCountry))) {
      return international; // digits carry the default country's own code
    }
    return national;
  }

  return national ?? international;
}

/**
 * Form validator that accepts any phone number libphonenumber recognises as
 * valid — including numbers already written in international/E.164 form with a
 * leading "+". Unlike the stricter `ngxMatInputTelValidator` (which parses
 * against the selected country and throws on partial input), this only asks
 * "is this a valid number?" and never rejects a value purely for starting with
 * "+". Emptiness is left to `Validators.required`.
 */
export function validPhoneNumberValidator(
  control: AbstractControl
): ValidationErrors | null {
  const value = (control.value ?? '').toString().trim();
  if (!value) return null;
  const parsed = parsePhoneNumberFromString(value);
  return parsed?.isValid() ? null : { validatePhoneNumber: true };
}

/**
 * Strips characters a phone number can't legally begin or end with — e.g. the
 * leading apostrophe Excel prepends (`'233…`), wrapping brackets/quotes, or a
 * stray trailing punctuation mark. A leading `+` and internal separators are
 * preserved so the number still parses. Only the boundaries are cleaned; the
 * inside of the string is left untouched.
 */
export function sanitizePhoneNumber(raw: string | null | undefined): string {
  if (raw === null || raw === undefined) return '';
  let value = String(raw).trim();
  if (!value) return '';
  // Drop leading characters that aren't a digit or the leading "+".
  value = value.replace(/^[^\d+]+/, '');
  // Only the first "+" is meaningful — collapse a leading run down to one.
  value = value.replace(/^\++/, '+');
  // Drop trailing characters that aren't a digit.
  value = value.replace(/[^\d]+$/, '');
  return value;
}

/**
 * Serializes a header row plus data rows into RFC-4180 CSV text. Cells that
 * contain a comma, quote or newline are wrapped in double quotes with internal
 * quotes doubled; columns are aligned to `headers` so ragged rows stay in sync.
 */
export function toCsv(headers: string[], rows: string[][]): string {
  const escape = (cell: unknown): string => {
    const s = cell === null || cell === undefined ? '' : String(cell);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.map(escape).join(',')];
  for (const row of rows) {
    lines.push(headers.map((_, i) => escape(row[i])).join(','));
  }
  return lines.join('\r\n');
}

// ---- Spreadsheet upload formats ------------------------------------------
// The file-upload features (contacts import, smart broadcast, phone-number
// import) all parse through SheetJS (`xlsx`), which reads far more than just
// CSV/XLS/XLSX. Keep the accepted set in one place so every uploader stays in
// sync. Extensions are lowercase, without the leading dot.
export const SPREADSHEET_EXTENSIONS = [
  'csv',
  'tsv',
  'xls',
  'xlsx',
  'xlsm',
  'xlsb',
  'xlt',
  'xltx',
  'xltm',
  'ods',
  'fods',
  'dif',
  'prn',
  'dbf',
] as const;

// Value for an `<input type="file" accept="…">` — the dotted extensions plus a
// few common MIME types so pickers on stricter platforms still recognise them.
export const SPREADSHEET_ACCEPT = [
  ...SPREADSHEET_EXTENSIONS.map((ext) => `.${ext}`),
  'text/csv',
  'text/tab-separated-values',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.oasis.opendocument.spreadsheet',
].join(',');

// The lowercase extension of a file name (without the dot), or '' if none.
export function getFileExtension(fileName: string): string {
  const parts = fileName.split('.');
  return parts.length > 1 ? (parts.pop() as string).toLowerCase() : '';
}

// True when the file's extension is one SheetJS can parse as a spreadsheet.
export function isSpreadsheetFile(fileName: string): boolean {
  return (SPREADSHEET_EXTENSIONS as readonly string[]).includes(
    getFileExtension(fileName)
  );
}

export function getCountryFlagEmoji(countryCode: string): string {
  return countryCode
    .toUpperCase()
    .replace(/./g, (char) =>
      String.fromCodePoint(0x1f1e6 + char.charCodeAt(0) - 0x41)
    );
}

/**
 * Converts a file size in bytes to a human-readable string format.
 * @param bytes - The file size in bytes.
 * @param decimals - Number of decimal places to include (default is 2).
 * @returns A formatted string like '4.2 KB', '5 MB', '1.3 GB', etc.
 */
export function formatFileSize(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];

  // Determine which unit to use
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  // Convert and round the number
  const size = parseFloat((bytes / Math.pow(k, i)).toFixed(decimals));

  return `${size} ${sizes[i]}`;
}

/**
 * Formats a raw phone number string exactly like the `display` field
 * in your CSV processing logic.
 *
 * @param rawPhoneNumber - The phone number as it appears in the CSV (with or without country code)
 * @returns Formatted international string if valid, otherwise the original input or '(empty)'
 */
export const formatPhoneNumberForDisplay = (rawPhoneNumber: string): string => {
  const trimmed = (rawPhoneNumber ?? '').trim();

  if (!trimmed) {
    return '(empty)';
  }

  const parsed = parsePhoneNumberFromString(trimmed);

  if (parsed?.isValid()) {
    return parsed.formatInternational(); // e.g. "+1 415 555 2671"
  }

  // Not a valid number → show exactly what user typed
  return trimmed;
};
